/**
 * InvoicePdfTemplate — React-PDF document component for rendering invoice PDFs.
 *
 * When an InvoiceTemplate + InvoicePreviewData are supplied in the data prop,
 * the component renders template-driven sections that mirror the visual output
 * of InvoicePreview's BuilderFormatPreview.  This ensures the saved PDF matches
 * the on-screen preview.
 *
 * When no template is provided the component falls back to the original
 * hardcoded layout so existing call-sites continue to work.
 */

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import type { InvoicePdfData } from '@/types/invoice-pdf';
import type {
  InvoiceTemplate,
  InvoicePreviewData,
  InvoiceLineItem as TemplateLineItem,
  GroupByOption,
} from '@/lib/invoice-template-types';
import { groupLineItems, resolveGroupLabel, getDescriptionTemplateForGroup } from '@/lib/invoice-group-utils';
import { getContrastTextColor } from '@/lib/utils';

// ============================================================================
// Shared helpers
// ============================================================================

function pdfFormatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(amount);
}

function pdfFormatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_match, dec: string) => String.fromCharCode(Number(dec)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)));
}

function stripHtml(html: string): string {
  return decodeHtmlEntities(html.replace(/<[^>]*>/g, ''));
}

// ============================================================================
// HTML → react-pdf node parser
// Handles the tags Tiptap actually produces:
//   block: <p>, <ul>, <ol>, <li>, <h1>–<h3>, <br>
//   inline: <strong>, <b>, <em>, <i>, <u>, <s>, <code>
// Nested combinations (e.g. <strong><em>text</em></strong>) are supported.
// ============================================================================

interface PdfTextStyle {
  fontFamily?: string;
  fontStyle?: 'normal' | 'italic';
  textDecoration?: string;
  backgroundColor?: string;
  fontSize?: number;
}

type PdfNode =
  | { type: 'text'; content: string; style: PdfTextStyle }
  | { type: 'block'; tag: string; children: PdfNode[] };

function parseHtmlToPdfNodes(html: string): PdfNode[] {
  if (!html) return [];

  // Normalise self-closing <br/> and <br />
  const normalised = html
    .replace(/<br\s*\/?>/gi, '<br>')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"');

  const INLINE_TAGS = new Set(['strong', 'b', 'em', 'i', 'u', 's', 'strike', 'code', 'span', 'br']);

  function parseInlineContent(rawHtml: string, inheritedStyle: PdfTextStyle = {}): PdfNode[] {
    const nodes: PdfNode[] = [];
    const tagRegex = /<(\/?)(\w+)([^>]*)>|([^<]+)/g;
    let match: RegExpExecArray | null;
    let currentStyle: PdfTextStyle = { ...inheritedStyle };
    const styleStack: PdfTextStyle[] = [];

    while ((match = tagRegex.exec(rawHtml)) !== null) {
      const [, closing, tag, , text] = match;
      const tagLower = tag?.toLowerCase();

      if (text !== undefined) {
        // Text node
        if (text.trim() || text.includes(' ')) {
          nodes.push({ type: 'text', content: text, style: { ...currentStyle } });
        }
      } else if (tagLower === 'br') {
        nodes.push({ type: 'text', content: '\n', style: { ...currentStyle } });
      } else if (INLINE_TAGS.has(tagLower) && !closing) {
        // Opening inline tag — push style
        styleStack.push({ ...currentStyle });
        if (tagLower === 'strong' || tagLower === 'b') {
          currentStyle = { ...currentStyle, fontFamily: 'Helvetica-Bold' };
        } else if (tagLower === 'em' || tagLower === 'i') {
          currentStyle = { ...currentStyle, fontStyle: 'italic' };
        } else if (tagLower === 'code') {
          currentStyle = { ...currentStyle, backgroundColor: '#f3f4f6', fontSize: 8 };
        }
        // bold+italic: Tiptap nests <strong><em> — handled naturally by style accumulation
      } else if (INLINE_TAGS.has(tagLower) && closing) {
        // Closing inline tag — pop style
        if (styleStack.length > 0) {
          currentStyle = styleStack.pop()!;
        }
      }
    }
    return nodes;
  }

  function buildBlockNodes(rawHtml: string): PdfNode[] {
    const result: PdfNode[] = [];
    const blockRegex = /<(p|ul|ol|li|h[123]|blockquote|div)([^>]*)>([\s\S]*?)<\/\1>|(<br>)/gi;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = blockRegex.exec(rawHtml)) !== null) {
      // Any inline content before this block
      const before = rawHtml.slice(lastIndex, match.index).trim();
      if (before) {
        const inlineNodes = parseInlineContent(before);
        if (inlineNodes.length > 0) {
          result.push({ type: 'block', tag: 'p', children: inlineNodes });
        }
      }

      if (match[4] === '<br>') {
        result.push({ type: 'block', tag: 'br', children: [] });
      } else {
        const tag = match[1].toLowerCase();
        const inner = match[3];

        if (tag === 'ul' || tag === 'ol') {
          // Recursively extract <li> items
          const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi;
          let liMatch: RegExpExecArray | null;
          while ((liMatch = liRegex.exec(inner)) !== null) {
            const liChildren = parseInlineContent(liMatch[1]);
            const bullet: PdfNode = {
              type: 'text',
              content: tag === 'ol' ? '  • ' : '  • ',
              style: {},
            };
            result.push({
              type: 'block',
              tag: 'li',
              children: [bullet, ...liChildren],
            });
          }
        } else {
          const inheritedStyle: PdfTextStyle =
            tag === 'h1' ? { fontFamily: 'Helvetica-Bold', fontSize: 14 }
            : tag === 'h2' ? { fontFamily: 'Helvetica-Bold', fontSize: 12 }
            : tag === 'h3' ? { fontFamily: 'Helvetica-Bold', fontSize: 11 }
            : {};
          const children = parseInlineContent(inner, inheritedStyle);
          result.push({ type: 'block', tag, children });
        }
      }

      lastIndex = match.index + match[0].length;
    }

    // Any remaining inline content after last block tag
    const trailing = rawHtml.slice(lastIndex).trim();
    if (trailing) {
      const inlineNodes = parseInlineContent(trailing);
      if (inlineNodes.length > 0) {
        result.push({ type: 'block', tag: 'p', children: inlineNodes });
      }
    }

    return result;
  }

  return buildBlockNodes(normalised);
}

// Renders the parsed node tree as react-pdf <Text> elements
function RichText({ html, baseStyle }: { html: string; baseStyle?: Style }) {
  const nodes = parseHtmlToPdfNodes(html);

  if (nodes.length === 0) return null;

  return (
    <>
      {nodes.map((block, blockIdx) => {
        if (block.type !== 'block') return null;

        if (block.tag === 'br') {
          return <Text key={blockIdx} style={{ fontSize: 4 }}>{' '}</Text>;
        }

        const blockMargin = block.tag === 'li' ? { marginBottom: 2 } : { marginBottom: 4 };

        return (
          <Text key={blockIdx} style={[baseStyle ?? {}, blockMargin]}>
            {block.children.map((child, childIdx) => {
              if (child.type !== 'text') return null;
              const s: Style = {};
              if (child.style.fontFamily) s.fontFamily = child.style.fontFamily;
              if (child.style.fontStyle === 'italic') {
                // react-pdf handles bold-italic via Helvetica-BoldOblique
                s.fontFamily = child.style.fontFamily === 'Helvetica-Bold'
                  ? 'Helvetica-BoldOblique'
                  : 'Helvetica-Oblique';
              }
              if (child.style.fontSize) s.fontSize = child.style.fontSize;
              return (
                <Text key={childIdx} style={s}>
                  {child.content}
                </Text>
              );
            })}
          </Text>
        );
      })}
    </>
  );
}

// ============================================================================
// Builder state extraction (mirrors InvoicePreview.extractBuilderState)
// ============================================================================

type PdfPosition = 'left' | 'center' | 'right';

interface PdfBuilderState {
  showCompanyLogo: boolean;
  logoPosition: PdfPosition;
  companyLogo: string;
  showCompanyName: boolean;
  showCompanyAddress: boolean;
  showCompanyPhone: boolean;
  showCompanyVAT: boolean;
  showCompanyRegistration: boolean;
  companyPosition: PdfPosition;
  showClientName: boolean;
  showClientAddress: boolean;
  showClientContact: boolean;
  clientPosition: PdfPosition;
  showInvoiceNumber: boolean;
  showInvoiceDate: boolean;
  showDueDate: boolean;
  invoiceDetailsPosition: PdfPosition;
  showProjectCode: boolean;
  showProjectName: boolean;
  showProjectReference: boolean;
  showProjectDescription: boolean;
  showProjectManager: boolean;
  projectPosition: PdfPosition;
  groupBy: GroupByOption;
  groupedRowDescription: string;
  groupedRowDescriptionExpense: string;
  groupedRowDescriptionPurchase: string;
  showDate: boolean;
  showDescription: boolean;
  showCostCentreCode: boolean;
  showCostCentreName: boolean;
  showPerson: boolean;
  showRole: boolean;
  showQty: boolean;
  showRate: boolean;
  showVAT: boolean;
  showVATCode: boolean;
  showAmount: boolean;
  showType: boolean;
  showSubtotal: boolean;
  showTax: boolean;
  showDiscount: boolean;
  showTotal: boolean;
  showBankDetails: boolean;
  showNotes: boolean;
  showTermsConditions: boolean;
  footerPosition: PdfPosition;
  notesContent: string;
  termsContent: string;
  sectionOrder: string[];
  alternateRowColors: boolean;
  showBorders: boolean;
}

const FIELD_TO_SHOW: Record<string, keyof PdfBuilderState> = {
  date: 'showDate',
  description: 'showDescription',
  costCentreCode: 'showCostCentreCode',
  costCentreName: 'showCostCentreName',
  person: 'showPerson',
  role: 'showRole',
  qty: 'showQty',
  rate: 'showRate',
  net: 'showRate',
  vat: 'showVAT',
  vatCode: 'showVATCode',
  amount: 'showAmount',
  type: 'showType',
};

function extractPdfBuilderState(template: InvoiceTemplate): PdfBuilderState {
  const findById = (id: string) =>
    template.sections.find(s => s.id === id) ??
    template.sections.find(s => s.type === id.replace(/([A-Z])/g, '-$1').toLowerCase()) ??
    template.sections.find(s => s.type === id);

  const state: PdfBuilderState = {
    showCompanyLogo: true,
    logoPosition: 'left',
    companyLogo: template.branding?.logo || '',
    showCompanyName: true,
    showCompanyAddress: true,
    showCompanyPhone: true,
    showCompanyVAT: true,
    showCompanyRegistration: true,
    companyPosition: 'left',
    showClientName: true,
    showClientAddress: true,
    showClientContact: true,
    clientPosition: 'left',
    showInvoiceNumber: true,
    showInvoiceDate: true,
    showDueDate: true,
    invoiceDetailsPosition: 'right',
    showProjectCode: true,
    showProjectName: true,
    showProjectReference: true,
    showProjectDescription: true,
    showProjectManager: true,
    projectPosition: 'left',
    groupBy: 'none',
    groupedRowDescription: '',
    groupedRowDescriptionExpense: '',
    groupedRowDescriptionPurchase: '',
    showDate: true,
    showDescription: true,
    showCostCentreCode: true,
    showCostCentreName: true,
    showPerson: true,
    showRole: true,
    showQty: true,
    showRate: true,
    showVAT: true,
    showVATCode: true,
    showAmount: true,
    showType: false,
    showSubtotal: true,
    showTax: true,
    showDiscount: true,
    showTotal: true,
    showBankDetails: false,
    showNotes: true,
    showTermsConditions: true,
    footerPosition: 'left',
    notesContent: '',
    termsContent: '',
    sectionOrder: ['logo', 'company', 'client', 'invoice', 'project', 'lineItems', 'totals', 'footer'],
    alternateRowColors: false,
    showBorders: true,
  };

  const logoSec = findById('logo');
  if (logoSec?.config) {
    const c = logoSec.config as unknown as Record<string, unknown>;
    if ('showLogo' in c) state.showCompanyLogo = c.showLogo as boolean;
    if ('showCompanyLogo' in c) state.showCompanyLogo = c.showCompanyLogo as boolean;
    if ('logoPosition' in c) state.logoPosition = c.logoPosition as PdfPosition;
  }

  const companySec = findById('company');
  if (companySec?.config) {
    const c = companySec.config as unknown as Record<string, unknown>;
    if ('showCompanyName' in c) state.showCompanyName = c.showCompanyName as boolean;
    if ('showCompanyAddress' in c) state.showCompanyAddress = c.showCompanyAddress as boolean;
    if ('showPhone' in c) state.showCompanyPhone = c.showPhone as boolean;
    if ('showCompanyPhone' in c) state.showCompanyPhone = c.showCompanyPhone as boolean;
    if ('showVAT' in c) state.showCompanyVAT = c.showVAT as boolean;
    if ('showRegistration' in c) state.showCompanyRegistration = c.showRegistration as boolean;
    if ('position' in c) state.companyPosition = c.position as PdfPosition;
  }

  const clientSec = findById('client');
  if (clientSec?.config) {
    const c = clientSec.config as unknown as Record<string, unknown>;
    if ('showClientName' in c) state.showClientName = c.showClientName as boolean;
    if ('showClientAddress' in c) state.showClientAddress = c.showClientAddress as boolean;
    if ('showClientContact' in c) state.showClientContact = c.showClientContact as boolean;
    if ('position' in c) state.clientPosition = c.position as PdfPosition;
  }

  const invoiceSec = findById('invoice');
  if (invoiceSec?.config) {
    const c = invoiceSec.config as unknown as Record<string, unknown>;
    if ('showInvoiceNumber' in c) state.showInvoiceNumber = c.showInvoiceNumber as boolean;
    if ('showInvoiceDate' in c) state.showInvoiceDate = c.showInvoiceDate as boolean;
    if ('showDueDate' in c) state.showDueDate = c.showDueDate as boolean;
    if ('position' in c) state.invoiceDetailsPosition = c.position as PdfPosition;
  }

  const projectSec = findById('project');
  if (projectSec?.config) {
    const c = projectSec.config as unknown as Record<string, unknown>;
    if ('showProjectCode' in c) state.showProjectCode = c.showProjectCode as boolean;
    if ('showProjectName' in c) state.showProjectName = c.showProjectName as boolean;
    if ('showProjectReference' in c) state.showProjectDescription = c.showProjectReference as boolean;
    if ('showProjectDescription' in c) state.showProjectDescription = c.showProjectDescription as boolean;
    if ('showProjectManager' in c) state.showProjectManager = c.showProjectManager as boolean;
    if ('position' in c) state.projectPosition = c.position as PdfPosition;
  }

  const lineItemsSec = findById('lineItems');
  if (lineItemsSec?.config) {
    const c = lineItemsSec.config as unknown as Record<string, unknown>;
    if ('groupBy' in c) state.groupBy = c.groupBy as GroupByOption;
    if ('groupedRowDescription' in c) state.groupedRowDescription = c.groupedRowDescription as string;
    if ('groupedRowDescriptionExpense' in c) state.groupedRowDescriptionExpense = c.groupedRowDescriptionExpense as string;
    if ('groupedRowDescriptionPurchase' in c) state.groupedRowDescriptionPurchase = c.groupedRowDescriptionPurchase as string;
    if ('alternateRowColors' in c) state.alternateRowColors = c.alternateRowColors as boolean;
    if ('showBorders' in c) state.showBorders = c.showBorders as boolean;
    if ('columns' in c && c.columns) {
      if (Array.isArray(c.columns)) {
        const st = state as unknown as Record<string, unknown>;
        (Object.values(FIELD_TO_SHOW) as (keyof PdfBuilderState)[]).forEach(key => { st[key] = false; });
        (c.columns as Array<{ id?: string; field?: string; visible?: boolean }>).forEach(col => {
          const fieldKey = col.field ?? col.id ?? '';
          const stateKey = FIELD_TO_SHOW[fieldKey];
          if (stateKey) st[stateKey] = col.visible ?? true;
        });
      } else if (typeof c.columns === 'object' && !Array.isArray(c.columns)) {
        const cols = c.columns as Record<string, boolean>;
        if ('date' in cols) state.showDate = cols.date;
        if ('description' in cols) state.showDescription = cols.description;
        if ('costCentreCode' in cols) state.showCostCentreCode = cols.costCentreCode;
        if ('costCentreName' in cols) state.showCostCentreName = cols.costCentreName;
        if ('person' in cols) state.showPerson = cols.person;
        if ('role' in cols) state.showRole = cols.role;
        if ('qty' in cols) state.showQty = cols.qty;
        if ('rate' in cols) state.showRate = cols.rate;
        if ('vat' in cols) state.showVAT = cols.vat;
        if ('vatCode' in cols) state.showVATCode = cols.vatCode;
        if ('amount' in cols) state.showAmount = cols.amount;
        if ('type' in cols) state.showType = cols.type;
      }
    }
  }

  const totalsSec = findById('totals');
  if (totalsSec?.config) {
    const c = totalsSec.config as unknown as Record<string, unknown>;
    if ('showSubtotal' in c) state.showSubtotal = c.showSubtotal as boolean;
    if ('showTax' in c) state.showTax = c.showTax as boolean;
    if ('showDiscount' in c) state.showDiscount = c.showDiscount as boolean;
    if ('showTotal' in c) state.showTotal = c.showTotal as boolean;
  }

  const footerSec = findById('footer');
  if (footerSec?.config) {
    const c = footerSec.config as unknown as Record<string, unknown>;
    if ('showBankDetails' in c) state.showBankDetails = c.showBankDetails as boolean;
    if ('showNotes' in c) state.showNotes = c.showNotes as boolean;
    if ('showTermsConditions' in c) state.showTermsConditions = c.showTermsConditions as boolean;
    if ('showTermsAndConditions' in c) state.showTermsConditions = c.showTermsAndConditions as boolean;
    if ('notesContent' in c) state.notesContent = c.notesContent as string;
    if ('notes' in c && !('notesContent' in c)) state.notesContent = c.notes as string;
    if ('termsAndConditions' in c) state.termsContent = c.termsAndConditions as string;
    if ('termsContent' in c) state.termsContent = c.termsContent as string;
    if ('paymentTerms' in c) state.notesContent = state.notesContent || (c.paymentTerms as string);
    if ('position' in c) state.footerPosition = c.position as PdfPosition;
  }

  if (template.branding?.logoPosition) {
    state.logoPosition = template.branding.logoPosition as PdfPosition;
  }

  if (template.settings?.groupBy) {
    state.groupBy = template.settings.groupBy as GroupByOption;
  }

  const builderIds = new Set(['logo', 'company', 'client', 'invoice', 'project', 'lineItems', 'totals', 'footer']);
  const ordered = template.sections
    .filter(sec => builderIds.has(sec.id))
    .sort((a, b) => a.order - b.order)
    .map(sec => sec.id);
  if (ordered.length > 0) {
    state.sectionOrder = ordered;
  }

  return state;
}

const PDF_DEFAULT_COLUMN_ORDER = [
  'date', 'description', 'person', 'role', 'qty', 'net', 'vat', 'vatCode', 'amount',
  'costCentreCode', 'costCentreName', 'type',
];

// ============================================================================
// Styles
// ============================================================================

const tplStyles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', color: '#1a1a1a' },
  mb8: { marginBottom: 8 },
  logoContainer: { marginBottom: 12 },
  logoImage: { height: 60, width: 'auto', maxWidth: 200, objectFit: 'contain' as const },
  logoPlaceholder: { height: 60, width: 150, backgroundColor: '#f1f5f9', justifyContent: 'center', alignItems: 'center' },
  companyName: { fontSize: 14, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
  companyDetail: { fontSize: 9, color: '#555555', lineHeight: 1.4 },
  sectionLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 4 },
  clientName: { fontSize: 11, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
  invoiceNumber: { fontSize: 20, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  invoiceMeta: { fontSize: 9, color: '#555555', lineHeight: 1.6 },
  projectLabel: { fontSize: 9, color: '#555555' },
  projectValue: { fontSize: 10, fontFamily: 'Helvetica-Bold' },
  tableContainer: { marginBottom: 20 },
  tableRow: { flexDirection: 'row' as const, minHeight: 24 },
  tableCell: { fontSize: 9, padding: 6 },
  tableCellBold: { fontSize: 9, fontFamily: 'Helvetica-Bold', padding: 6 },
  headerCell: { fontSize: 8, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase' as const, letterSpacing: 0.5, padding: 6 },
  rightAlign: { textAlign: 'right' as const },
  totalsContainer: { alignItems: 'flex-end' as const, marginBottom: 20 },
  totalsTable: { width: 220 },
  totalsRow: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, paddingVertical: 3, paddingHorizontal: 6 },
  totalsLabel: { fontSize: 9, color: '#555555' },
  totalsValue: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  totalsDivider: { borderBottomWidth: 2, marginVertical: 3 },
  grandTotalRow: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, paddingVertical: 5, paddingHorizontal: 6 },
  grandTotalLabel: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  grandTotalValue: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  footerSection: { marginTop: 12 },
  footerLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 3 },
  footerText: { fontSize: 9, color: '#555555', lineHeight: 1.4 },
  pageFooter: { position: 'absolute' as const, bottom: 30, left: 40, right: 40, borderTopWidth: 1, borderTopColor: '#e5e7eb', paddingTop: 8, flexDirection: 'row' as const, justifyContent: 'space-between' as const },
  pageFooterText: { fontSize: 7, color: '#9ca3af' },
});

// ============================================================================
// Position helper — mirrors InvoicePreview.positionClasses for react-pdf
// ============================================================================

function pdfPositionStyle(position: PdfPosition): {
  alignItems: 'flex-start' | 'center' | 'flex-end';
  textAlign: 'left' | 'center' | 'right';
} {
  switch (position) {
    case 'center':
      return { alignItems: 'center', textAlign: 'center' };
    case 'right':
      return { alignItems: 'flex-end', textAlign: 'right' };
    default:
      return { alignItems: 'flex-start', textAlign: 'left' };
  }
}

// ============================================================================
// Template-Driven PDF
// ============================================================================

interface TemplateProps {
  template: InvoiceTemplate;
  previewData: InvoicePreviewData;
  notes?: string;
}

function TemplateDrivenPdf({ template, previewData, notes }: TemplateProps) {
  const state = extractPdfBuilderState(template);
  // Fall back to company logo from previewData when template has no explicit logo
  if (template.branding?.logo === undefined && previewData.company.logo) {
    state.companyLogo = previewData.company.logo;
  }
  const accentBg = template.branding?.primaryColor || '#2563eb';
  const accentText = getContrastTextColor(accentBg);

  const lineItemsSec = template.sections.find(sec => sec.id === 'lineItems' || sec.type === 'line-items');
  let columnOrder = PDF_DEFAULT_COLUMN_ORDER;
  if (lineItemsSec?.config) {
    const c = lineItemsSec.config as unknown as Record<string, unknown>;
    if (Array.isArray(c.columnOrder) && c.columnOrder.length > 0) {
      columnOrder = c.columnOrder as string[];
    }
  }

  type ColDef = { show: boolean; label: string; align: 'left' | 'right' };
  const colDefs: Record<string, ColDef> = {
    date:           { show: state.showDate,           label: 'Date',        align: 'left' },
    description:    { show: state.showDescription,    label: 'Description', align: 'left' },
    person:         { show: state.showPerson,         label: 'Person',      align: 'left' },
    role:           { show: state.showRole,            label: 'Role',        align: 'left' },
    qty:            { show: state.showQty,             label: 'Qty',         align: 'right' },
    net:            { show: state.showRate,            label: 'Rate',        align: 'right' },
    vat:            { show: state.showVAT,             label: 'VAT',         align: 'right' },
    vatCode:        { show: state.showVATCode,         label: 'VAT Code',    align: 'left' },
    amount:         { show: state.showAmount,          label: 'Amount',      align: 'right' },
    costCentreCode: { show: state.showCostCentreCode,  label: 'CC Code',     align: 'left' },
    costCentreName: { show: state.showCostCentreName,  label: 'CC Name',     align: 'left' },
    type:           { show: state.showType,             label: 'Type',        align: 'left' },
  };

  const isGroupedOut = (f: string) =>
    state.groupBy !== 'none' && f !== 'description' && f !== 'amount';

  const visibleCols = columnOrder.filter(f => colDefs[f]?.show && !isGroupedOut(f));
  const colWidth = `${Math.floor(100 / Math.max(visibleCols.length, 1))}%`;

  const grouped = groupLineItems(previewData.lineItems, state.groupBy);

  function getCellValue(item: TemplateLineItem, field: string): string {
    switch (field) {
      case 'date': return item.date;
      case 'description': return item.description;
      case 'person': return item.person || '-';
      case 'role': return item.role || '-';
      case 'qty': return item.qty.toFixed(1);
      case 'net': return pdfFormatCurrency(item.net);
      case 'vat': return pdfFormatCurrency(item.vat);
      case 'vatCode': return item.vatCode;
      case 'amount': return pdfFormatCurrency(item.amount);
      case 'costCentreCode': return item.costCentreCode || '';
      case 'costCentreName': return item.costCentreName || '';
      case 'type': { const t = item.lineItemType ?? 'timesheet'; return t === 'timesheet' ? 'Timesheet' : t === 'expense' ? 'Expense' : 'Purchase'; }
      default: return '';
    }
  }

  type GroupItem = NonNullable<ReturnType<typeof groupLineItems>>[number];

  function getGroupCellValue(group: GroupItem, field: string): string {
    if (field === 'description') {
      const tpl = getDescriptionTemplateForGroup(
        group.items,
        state.groupedRowDescription,
        state.groupedRowDescriptionExpense,
        state.groupedRowDescriptionPurchase,
      );
      const resolved = tpl
        ? resolveGroupLabel(stripHtml(tpl), group.items, {
            totalQty: group.totalQty,
            subtotalAmount: group.subtotalAmount,
            itemCount: group.itemCount,
          })
        : group.groupLabel;
      return resolved || group.groupLabel;
    }
    if (field === 'amount') {
      return pdfFormatCurrency(group.subtotalAmount);
    }
    return '';
  }

  const altRowBg = state.alternateRowColors ? '#f9fafb' : undefined;

  const borderStyle = state.showBorders
    ? { borderWidth: 0.5, borderColor: '#d1d5db' }
    : { borderBottomWidth: 0.5, borderBottomColor: '#f3f4f6' };

  // --- Section renderers ---

  const renderLogo = () => {
    if (!state.showCompanyLogo) return null;
    // Fallback: template logo → General Settings logo → hide entirely
    const effectiveLogo = state.companyLogo || previewData.company.logo;
    if (!effectiveLogo) return null;
    const pos = pdfPositionStyle(state.logoPosition);
    return (
      <View style={[tplStyles.logoContainer, { alignItems: pos.alignItems }]}>
        <Image src={effectiveLogo} style={tplStyles.logoImage} />
      </View>
    );
  };

  const renderCompany = () => {
    if (!state.showCompanyName && !state.showCompanyAddress && !state.showCompanyPhone && !state.showCompanyVAT && !state.showCompanyRegistration) return null;
    const pos = pdfPositionStyle(state.companyPosition);
    return (
      <View style={[tplStyles.mb8, { alignItems: pos.alignItems }]}>
        {state.showCompanyName && <Text style={[tplStyles.companyName, { textAlign: pos.textAlign }]}>{previewData.company.name}</Text>}
        {state.showCompanyAddress && <Text style={[tplStyles.companyDetail, { textAlign: pos.textAlign }]}>{previewData.company.address}</Text>}
        {state.showCompanyPhone && previewData.company.phone && (
          <Text style={[tplStyles.companyDetail, { textAlign: pos.textAlign }]}>
            {previewData.company.phone}{previewData.company.email ? ` | ${previewData.company.email}` : ''}
          </Text>
        )}
        {state.showCompanyVAT && previewData.company.vatNumber && (
          <Text style={[tplStyles.companyDetail, { textAlign: pos.textAlign }]}>VAT Reg: {previewData.company.vatNumber}</Text>
        )}
        {state.showCompanyRegistration && previewData.company.registrationNumber && (
          <Text style={[tplStyles.companyDetail, { textAlign: pos.textAlign }]}>Company Reg: {previewData.company.registrationNumber}</Text>
        )}
      </View>
    );
  };

  const renderClient = () => {
    if (!state.showClientName && !state.showClientAddress && !state.showClientContact) return null;
    const pos = pdfPositionStyle(state.clientPosition);
    return (
      <View style={[tplStyles.mb8, { alignItems: pos.alignItems }]}>
        <Text style={[tplStyles.sectionLabel, { textAlign: pos.textAlign }]}>Bill To:</Text>
        {state.showClientName && <Text style={[tplStyles.clientName, { textAlign: pos.textAlign }]}>{previewData.client.name}</Text>}
        {state.showClientAddress && <Text style={[tplStyles.companyDetail, { textAlign: pos.textAlign }]}>{previewData.client.address}</Text>}
        {state.showClientContact && previewData.client.contact && (
          <Text style={[tplStyles.companyDetail, { textAlign: pos.textAlign }]}>Contact: {previewData.client.contact}</Text>
        )}
      </View>
    );
  };

  const renderInvoiceDetails = () => {
    if (!state.showInvoiceNumber && !state.showInvoiceDate && !state.showDueDate) return null;
    const pos = pdfPositionStyle(state.invoiceDetailsPosition);
    return (
      <View style={[tplStyles.mb8, { alignItems: pos.alignItems }]}>
        {state.showInvoiceNumber && <Text style={[tplStyles.invoiceNumber, { textAlign: pos.textAlign }]}>{previewData.invoice.number}</Text>}
        {state.showInvoiceDate && <Text style={[tplStyles.invoiceMeta, { textAlign: pos.textAlign }]}>Date: {previewData.invoice.date}</Text>}
        {state.showDueDate && <Text style={[tplStyles.invoiceMeta, { textAlign: pos.textAlign }]}>Due: {previewData.invoice.dueDate}</Text>}
      </View>
    );
  };

  const renderProject = () => {
    if (!state.showProjectCode && !state.showProjectName && !state.showProjectDescription && !state.showProjectManager) return null;
    const pos = pdfPositionStyle(state.projectPosition);
    return (
      <View style={[tplStyles.mb8, { alignItems: pos.alignItems }]}>
        {(state.showProjectCode || state.showProjectName) && (
          <View style={{ flexDirection: 'row' as const }}>
            <Text style={tplStyles.projectLabel}>Project: </Text>
            <Text style={tplStyles.projectValue}>{previewData.project.name}</Text>
          </View>
        )}
        {state.showProjectDescription && previewData.project.description && (
          <View style={{ flexDirection: 'row' as const }}>
            <Text style={tplStyles.projectLabel}>Description: </Text>
            <Text style={{ fontSize: 9 }}>{previewData.project.description}</Text>
          </View>
        )}
        {state.showProjectManager && previewData.project.projectManager && (
          <View style={{ flexDirection: 'row' as const }}>
            <Text style={tplStyles.projectLabel}>Project Manager: </Text>
            <Text style={{ fontSize: 9 }}>{previewData.project.projectManager}</Text>
          </View>
        )}
      </View>
    );
  };

  const renderLineItems = () => {
    if (visibleCols.length === 0) return null;
    return (
      <View style={tplStyles.tableContainer}>
        <View style={[tplStyles.tableRow, { backgroundColor: accentBg }]}>
          {visibleCols.map(field => {
            const col = colDefs[field];
            return (
              <View key={field} style={[{ width: colWidth }, col.align === 'right' ? tplStyles.rightAlign : {}]}>
                <Text style={[tplStyles.headerCell, { color: accentText }]}>{col.label}</Text>
              </View>
            );
          })}
        </View>

        {grouped === null
          ? previewData.lineItems.map((item, idx) => (
              <View
                key={item.id}
                style={[
                  tplStyles.tableRow,
                  borderStyle,
                  idx % 2 !== 0 && altRowBg ? { backgroundColor: altRowBg } : {},
                ]}
              >
                {visibleCols.map(field => {
                  const col = colDefs[field];
                  return (
                    <View key={field} style={[{ width: colWidth }, col.align === 'right' ? tplStyles.rightAlign : {}]}>
                      <Text style={tplStyles.tableCell}>{getCellValue(item, field)}</Text>
                    </View>
                  );
                })}
              </View>
            ))
          : grouped.map((group, idx) => (
              <View
                key={`group-${group.groupKey}`}
                style={[
                  tplStyles.tableRow,
                  borderStyle,
                  idx % 2 !== 0 && altRowBg ? { backgroundColor: altRowBg } : {},
                ]}
              >
                {visibleCols.map(field => {
                  const col = colDefs[field];
                  const val = getGroupCellValue(group, field);
                  return (
                    <View key={field} style={[{ width: colWidth }, col.align === 'right' ? tplStyles.rightAlign : {}]}>
                      <Text style={val ? tplStyles.tableCellBold : tplStyles.tableCell}>{val}</Text>
                    </View>
                  );
                })}
              </View>
            ))
        }
      </View>
    );
  };

  const renderTotals = () => {
    return (
      <View style={tplStyles.totalsContainer}>
        <View style={tplStyles.totalsTable}>
          {state.showSubtotal && (
            <View style={tplStyles.totalsRow}>
              <Text style={tplStyles.totalsLabel}>Subtotal</Text>
              <Text style={tplStyles.totalsValue}>{pdfFormatCurrency(previewData.totals.subtotal)}</Text>
            </View>
          )}
          {state.showTax && (
            <View style={tplStyles.totalsRow}>
              <Text style={tplStyles.totalsLabel}>VAT (20%)</Text>
              <Text style={tplStyles.totalsValue}>{pdfFormatCurrency(previewData.totals.tax)}</Text>
            </View>
          )}
          {state.showDiscount && previewData.totals.discount > 0 && (
            <View style={tplStyles.totalsRow}>
              <Text style={tplStyles.totalsLabel}>Discount</Text>
              <Text style={tplStyles.totalsValue}>-{pdfFormatCurrency(previewData.totals.discount)}</Text>
            </View>
          )}
          {state.showTotal && (
            <>
              <View style={[tplStyles.totalsDivider, { borderBottomColor: accentBg }]} />
              <View style={tplStyles.grandTotalRow}>
                <Text style={[tplStyles.grandTotalLabel, { color: accentBg }]}>Total</Text>
                <Text style={tplStyles.grandTotalValue}>{pdfFormatCurrency(previewData.totals.total)}</Text>
              </View>
            </>
          )}
        </View>
      </View>
    );
  };

  const renderFooter = () => {
    if (!state.showBankDetails && !state.showNotes && !state.showTermsConditions) return null;
    const pos = pdfPositionStyle(state.footerPosition);
    return (
      <View wrap={false} style={[tplStyles.footerSection, { alignItems: pos.alignItems }]}>
        {state.showBankDetails && previewData.company.bankDetails && (
          <View style={tplStyles.mb8}>
            <Text style={tplStyles.footerLabel}>Bank Details</Text>
            {previewData.company.bankDetails.bankName && (
              <Text style={tplStyles.footerText}>{previewData.company.bankDetails.bankName}</Text>
            )}
            {previewData.company.bankDetails.accountName && (
              <Text style={tplStyles.footerText}>Account Name: {previewData.company.bankDetails.accountName}</Text>
            )}
            {previewData.company.bankDetails.sortCode && (
              <Text style={tplStyles.footerText}>Sort Code: {previewData.company.bankDetails.sortCode}</Text>
            )}
            {previewData.company.bankDetails.accountNumber && (
              <Text style={tplStyles.footerText}>Account No: {previewData.company.bankDetails.accountNumber}</Text>
            )}
            {previewData.company.bankDetails.iban && (
              <Text style={tplStyles.footerText}>IBAN: {previewData.company.bankDetails.iban}</Text>
            )}
            {previewData.company.bankDetails.swiftBic && (
              <Text style={tplStyles.footerText}>SWIFT/BIC: {previewData.company.bankDetails.swiftBic}</Text>
            )}
          </View>
        )}
        {state.showNotes && (
          <View style={tplStyles.mb8}>
            <Text style={tplStyles.footerLabel}>Notes</Text>
            <View style={{ borderWidth: 1.5, borderColor: '#9ca3af', borderStyle: 'dashed' as const, borderRadius: 4, padding: 8, minHeight: 30 }}>
              {(state.notesContent || notes)
                ? <>
                    {state.notesContent && <RichText html={state.notesContent} baseStyle={tplStyles.footerText} />}
                    {notes && <Text style={tplStyles.footerText}>{notes}</Text>}
                  </>
                : <Text style={{ fontSize: 9, color: '#9ca3af' }}>No notes</Text>
              }
            </View>
          </View>
        )}
        {state.showTermsConditions && state.termsContent && (
          <View style={tplStyles.mb8}>
            <Text style={tplStyles.footerLabel}>Terms and Conditions</Text>
            <RichText html={state.termsContent} baseStyle={tplStyles.footerText} />
          </View>
        )}
      </View>
    );
  };

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case 'logo': return renderLogo();
      case 'company': return renderCompany();
      case 'client': return renderClient();
      case 'invoice': return renderInvoiceDetails();
      case 'project': return renderProject();
      case 'lineItems': return renderLineItems();
      case 'totals': return renderTotals();
      case 'footer': return renderFooter();
      default: return null;
    }
  };

  const orientation = template.settings?.orientation || 'portrait';

  return (
    <Document>
      <Page size="A4" orientation={orientation} style={tplStyles.page}>
        {state.sectionOrder.map(sectionId => (
          <View key={sectionId}>
            {renderSection(sectionId)}
          </View>
        ))}

        <View style={tplStyles.pageFooter} fixed>
          <Text style={tplStyles.pageFooterText}>
            {previewData.company.name} | {previewData.invoice.number}
          </Text>
          <Text
            style={tplStyles.pageFooterText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}

// ============================================================================
// Fallback (original hardcoded layout)
// ============================================================================

const fallbackStyles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', color: '#1a1a1a' },
  header: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 30 },
  companyInfo: { flex: 1 },
  companyName: { fontSize: 18, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  companyAddress: { fontSize: 9, color: '#555555', lineHeight: 1.4 },
  invoiceTitle: { textAlign: 'right' as const },
  invoiceTitleText: { fontSize: 28, fontFamily: 'Helvetica-Bold', color: '#2563eb', marginBottom: 8 },
  invoiceMeta: { fontSize: 9, color: '#555555', textAlign: 'right' as const, lineHeight: 1.6 },
  invoiceMetaLabel: { fontFamily: 'Helvetica-Bold', color: '#1a1a1a' },
  divider: { borderBottomWidth: 1, borderBottomColor: '#e5e7eb', marginVertical: 16 },
  billToSection: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 24 },
  billToBlock: { flex: 1 },
  billToLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 6 },
  billToName: { fontSize: 12, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
  projectBlock: { flex: 1, alignItems: 'flex-end' as const },
  table: { marginBottom: 24 },
  tableHeader: { flexDirection: 'row', backgroundColor: '#2563eb', paddingVertical: 8, paddingHorizontal: 10 },
  tableHeaderCell: { color: '#ffffff', fontSize: 8, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  tableRow: { flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 10, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  tableRowAlt: { backgroundColor: '#f9fafb' },
  tableCell: { fontSize: 9 },
  colDescription: { width: '40%' },
  colQuantity: { width: '12%', textAlign: 'right' as const },
  colPrice: { width: '16%', textAlign: 'right' as const },
  colValue: { width: '16%', textAlign: 'right' as const },
  colCode: { width: '16%' },
  totalsSection: { alignItems: 'flex-end' as const, marginBottom: 24 },
  totalsTable: { width: 240 },
  totalsRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4, paddingHorizontal: 8 },
  totalsLabel: { fontSize: 9, color: '#555555' },
  totalsValue: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  totalsDivider: { borderBottomWidth: 2, borderBottomColor: '#2563eb', marginVertical: 4 },
  grandTotalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, paddingHorizontal: 8, backgroundColor: '#eff6ff' },
  grandTotalLabel: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#2563eb' },
  grandTotalValue: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#2563eb' },
  notesSection: { marginTop: 16 },
  notesLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 4 },
  notesText: { fontSize: 9, color: '#555555', lineHeight: 1.4 },
  footer: { position: 'absolute' as const, bottom: 30, left: 40, right: 40, borderTopWidth: 1, borderTopColor: '#e5e7eb', paddingTop: 8, flexDirection: 'row', justifyContent: 'space-between' },
  footerText: { fontSize: 7, color: '#9ca3af' },
  footerPage: { fontSize: 7, color: '#9ca3af' },
});

function FallbackPdf({ data }: { data: InvoicePdfData }) {
  const { transaction, companyDetails, lineItems, paymentTerms, notes } = data;

  const subtotal = lineItems.reduce((sum, line) => sum + line.value, 0);
  const vatRate = 0.2;
  const vatAmount = lineItems.some(line => line.taxAmount != null && line.taxAmount > 0)
    ? lineItems.reduce((sum, line) => sum + (line.taxAmount ?? 0), 0)
    : subtotal * vatRate;
  const grandTotal = subtotal + vatAmount;

  const dueDate = transaction.dueDate
    ? transaction.dueDate
    : (() => {
        const txDate = new Date(transaction.date);
        const d = new Date(txDate);
        d.setDate(d.getDate() + 30);
        return d.toISOString();
      })();

  return (
    <Document>
      <Page size="A4" style={fallbackStyles.page}>
        <View style={fallbackStyles.header}>
          <View style={fallbackStyles.companyInfo}>
            <Text style={fallbackStyles.companyName}>{companyDetails.name}</Text>
            <Text style={fallbackStyles.companyAddress}>
              {companyDetails.addressLine1}
              {companyDetails.addressLine2 ? `\n${companyDetails.addressLine2}` : ''}
              {`\n${companyDetails.city}, ${companyDetails.postcode}`}
              {`\n${companyDetails.country}`}
              {companyDetails.phone ? `\nTel: ${companyDetails.phone}` : ''}
              {companyDetails.email ? `\n${companyDetails.email}` : ''}
            </Text>
            {companyDetails.vatNumber && (
              <Text style={[fallbackStyles.companyAddress, { marginTop: 4 }]}>
                VAT No: {companyDetails.vatNumber}
              </Text>
            )}
            {companyDetails.companyNumber && (
              <Text style={fallbackStyles.companyAddress}>
                Company No: {companyDetails.companyNumber}
              </Text>
            )}
          </View>
          <View style={fallbackStyles.invoiceTitle}>
            <Text style={fallbackStyles.invoiceTitleText}>INVOICE</Text>
            <Text style={fallbackStyles.invoiceMeta}>
              <Text style={fallbackStyles.invoiceMetaLabel}>Invoice No: </Text>
              {transaction.invoiceNumber}
              {'\n'}
              <Text style={fallbackStyles.invoiceMetaLabel}>Date: </Text>
              {pdfFormatDate(transaction.date)}
              {'\n'}
              <Text style={fallbackStyles.invoiceMetaLabel}>Due Date: </Text>
              {pdfFormatDate(dueDate)}
              {'\n'}
              <Text style={fallbackStyles.invoiceMetaLabel}>Status: </Text>
              {transaction.status}
            </Text>
          </View>
        </View>

        <View style={fallbackStyles.divider} />

        <View style={fallbackStyles.billToSection}>
          <View style={fallbackStyles.billToBlock}>
            <Text style={fallbackStyles.billToLabel}>Bill To</Text>
            <Text style={fallbackStyles.billToName}>
              {transaction.accountName || 'Client'}
            </Text>
          </View>
          {transaction.projectName && (
            <View style={fallbackStyles.projectBlock}>
              <Text style={fallbackStyles.billToLabel}>Project</Text>
              <Text style={fallbackStyles.billToName}>{transaction.projectName}</Text>
            </View>
          )}
        </View>

        <View style={fallbackStyles.table}>
          <View style={fallbackStyles.tableHeader}>
            <Text style={[fallbackStyles.tableHeaderCell, fallbackStyles.colDescription]}>Description</Text>
            <Text style={[fallbackStyles.tableHeaderCell, fallbackStyles.colCode]}>Code</Text>
            <Text style={[fallbackStyles.tableHeaderCell, fallbackStyles.colQuantity]}>Qty</Text>
            <Text style={[fallbackStyles.tableHeaderCell, fallbackStyles.colPrice]}>Unit Price</Text>
            <Text style={[fallbackStyles.tableHeaderCell, fallbackStyles.colValue]}>Value</Text>
          </View>
          {lineItems.map((line, index) => (
            <View
              key={index}
              style={[
                fallbackStyles.tableRow,
                index % 2 === 1 ? fallbackStyles.tableRowAlt : {},
              ]}
            >
              <Text style={[fallbackStyles.tableCell, fallbackStyles.colDescription]}>{line.description}</Text>
              <Text style={[fallbackStyles.tableCell, fallbackStyles.colCode]}>{line.code}</Text>
              <Text style={[fallbackStyles.tableCell, fallbackStyles.colQuantity]}>{line.quantity.toFixed(2)}</Text>
              <Text style={[fallbackStyles.tableCell, fallbackStyles.colPrice]}>{pdfFormatCurrency(line.unitPrice)}</Text>
              <Text style={[fallbackStyles.tableCell, fallbackStyles.colValue]}>{pdfFormatCurrency(line.value)}</Text>
            </View>
          ))}
        </View>

        <View style={fallbackStyles.totalsSection}>
          <View style={fallbackStyles.totalsTable}>
            <View style={fallbackStyles.totalsRow}>
              <Text style={fallbackStyles.totalsLabel}>Subtotal</Text>
              <Text style={fallbackStyles.totalsValue}>{pdfFormatCurrency(subtotal)}</Text>
            </View>
            <View style={fallbackStyles.totalsRow}>
              <Text style={fallbackStyles.totalsLabel}>VAT ({(vatRate * 100).toFixed(0)}%)</Text>
              <Text style={fallbackStyles.totalsValue}>{pdfFormatCurrency(vatAmount)}</Text>
            </View>
            <View style={fallbackStyles.totalsDivider} />
            <View style={fallbackStyles.grandTotalRow}>
              <Text style={fallbackStyles.grandTotalLabel}>Total Due</Text>
              <Text style={fallbackStyles.grandTotalValue}>{pdfFormatCurrency(grandTotal)}</Text>
            </View>
          </View>
        </View>

        <View wrap={false}>
          {paymentTerms && (
            <View style={fallbackStyles.notesSection}>
              <Text style={fallbackStyles.notesLabel}>Payment Terms</Text>
              <Text style={fallbackStyles.notesText}>{paymentTerms}</Text>
            </View>
          )}

          {notes && (
            <View style={fallbackStyles.notesSection}>
              <Text style={fallbackStyles.notesLabel}>Notes</Text>
              <Text style={fallbackStyles.notesText}>{notes}</Text>
            </View>
          )}
        </View>

        <View style={fallbackStyles.footer} fixed>
          <Text style={fallbackStyles.footerText}>
            {companyDetails.name} | {transaction.invoiceNumber}
          </Text>
          <Text
            style={fallbackStyles.footerPage}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}

// ============================================================================
// Exported component — routes to template-driven or fallback
// ============================================================================

interface InvoicePdfTemplateProps {
  data: InvoicePdfData;
}

export function InvoicePdfTemplate({ data }: InvoicePdfTemplateProps) {
  if (data.template && data.previewData) {
    return <TemplateDrivenPdf template={data.template} previewData={data.previewData} notes={data.notes} />;
  }
  return <FallbackPdf data={data} />;
}
